<template>
  <div class="dropdown ms-sm-2 header-item notification-dropdown">
    <!-- Notification button -->
    <button
      type="button"
      class="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
      id="page-header-notification-dropdown"
      data-bs-toggle="dropdown"
      data-bs-auto-close="outside"
      aria-haspopup="true"
      aria-expanded="false"
      @click="handleOpen"
    >
      <i class="bx bx-bell fs-22"></i>

      <!-- Unread badge -->
      <span
        v-if="unreadCount > 0"
        class="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
        <span class="visually-hidden">unread notifications</span>
      </span>
    </button>

    <!-- Dropdown -->
    <div
      class="dropdown-menu dropdown-menu-end dropdown-menu-lg p-0 notification-dropdown-menu"
      aria-labelledby="page-header-notification-dropdown"
    >
      <!-- Header -->
      <div class="dropdown-head bg-primary bg-pattern rounded-top">
        <div class="p-3">
          <div class="row align-items-center">
            <div class="col">
              <h6 class="m-0 fs-16 fw-semibold text-white">
                Notifications
              </h6>
            </div>

            <div class="col-auto">
              <span
                v-if="unreadCount > 0"
                class="badge bg-light-subtle text-primary fs-12"
              >
                {{ unreadCount }} Unread
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div
        v-if="loading"
        class="notification-loading text-center py-5"
      >
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>

        <div class="text-muted mt-2">
          Loading notifications...
        </div>
      </div>

      <!-- Notification list -->
      <div
        v-else-if="notifications.length > 0"
        class="notification-list"
      >
        <div class="d-flex justify-content-end px-3 py-2 border-bottom">
          <button
            v-if="unreadCount > 0"
            type="button"
            class="btn btn-link btn-sm text-decoration-none p-0"
            @click="markAllAsRead"
          >
            Mark all as read
          </button>
        </div>

        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ unread: !notification.read_at }"
          @click="openNotification(notification)"
        >
          <div class="d-flex align-items-start px-2 py-1">
            
            <!-- Content -->
            <div class="flex-grow-1 ms-3 overflow-hidden">
              <div class="d-flex align-items-center">
                <h6
                  class="mb-1 fs-13 text-truncate"
                  :class="{
                    'fw-semibold': !notification.read_at,
                  }"
                >
                  {{ notification.data.title }}
                </h6>

                <span
                  v-if="!notification.read_at"
                  class="notification-unread-dot ms-2"
                ></span>
              </div>

              <p class="text-muted mb-1 fs-12 notification-message">
                {{ notification.data.description }}
              </p>

              <small class="text-muted fs-11">
                {{ formatTime(notification.created_at) }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div
        v-else
        class="text-center py-5 px-3"
      >
        <div class="avatar-sm mx-auto mb-3">
          <div class="avatar-title bg-light text-muted rounded-circle fs-20">
            <i class="bx bx-bell-off"></i>
          </div>
        </div>

        <h6 class="mb-1">
          No notifications
        </h6>

        <p class="text-muted mb-0 fs-13">
          You're all caught up.
        </p>
      </div>

      
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "NotificationDropdown",

  data() {
    return {
      loading: false,
      notifications: [],
      notificationPollingInterval: null,

    };
  },

  computed: {
    unreadCount() {
      return this.notifications.filter(
        notification => !notification.read_at
      ).length;
    },
  },

  mounted() {
    this.loadNotifications(true);

    // Poll every 60 seconds
    this.notificationPollingInterval = setInterval(() => {
        this.loadNotifications(false);
    }, 60 * 1000);
  },

  beforeUnmount() {
    if (this.notificationPollingInterval) {
        clearInterval(this.notificationPollingInterval);
        this.notificationPollingInterval = null;
    }
  },

  methods: {
    async handleOpen() {
      // Refresh when user opens the dropdown.
      await this.loadNotifications(false);
    },

    async loadNotifications(showLoading = false) {
      try {
        if(showLoading) {
            this.loading = true;
        }

        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        const response = await axios.get(process.env.VUE_APP_API_BASEURL + 'inboxes');
        this.notifications = response.data.data;

      } catch (error) {
        console.error(
          "Failed to load notifications:",
          error
        );
      } finally {
        if(showLoading) {
            this.loading = false;
        }
      }
    },

    async openNotification(notification) {
      try {
        // Mark as read
        if (!notification.read_at) {
          await this.markAsRead(notification);
        }

        // Navigate if notification has a URL
        if (notification.data.url) {
          this.$router.push(notification.data.url);
        }
      } catch (error) {
        console.error(
          "Failed to open notification:",
          error
        );
      }
    },

    async markAsRead(notification) {
      try {
               
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        await axios.post(process.env.VUE_APP_API_BASEURL + `inboxes/${notification.id}/read`);

      } catch (error) {
        console.error(
          "Failed to mark as read:",
          error
        );
      }

      notification.read_at = new Date().toISOString();
    },

    async markAllAsRead() {
      try {
        
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        await axios.post(process.env.VUE_APP_API_BASEURL + `inboxes/mark-all-as-read`);

        const now = new Date().toISOString();

        this.notifications.forEach(notification => {
          if (!notification.read_at) {
            notification.read_at = now;
          }
        });
      } catch (error) {
        console.error(
          "Failed to mark notifications as read:",
          error
        );
      }
    },

    closeDropdown() {
      const button = document.getElementById(
        "page-header-notification-dropdown"
      );

      if (!button) {
        return;
      }

      const dropdown =
        window.bootstrap?.Dropdown.getInstance(button);

      if (dropdown) {
        dropdown.hide();
      }
    },

    getIcon(type) {
      const icons = {
        payment: "bx bx-wallet",
        invoice: "bx bx-receipt",
        account: "bx bx-user",
        system: "bx bx-cog",
        warning: "bx bx-error",
        success: "bx bx-check-circle",
        default: "bx bx-bell",
      };

      return icons[type] || icons.default;
    },

    getIconClass(type) {
      const classes = {
        payment: "bg-success-subtle text-success",
        invoice: "bg-danger-subtle text-danger",
        account: "bg-primary-subtle text-primary",
        system: "bg-secondary-subtle text-secondary",
        warning: "bg-warning-subtle text-warning",
        success: "bg-success-subtle text-success",
        default: "bg-light text-muted",
      };

      return classes[type] || classes.default;
    },

    formatTime(date) {
      if (!date) {
        return "";
      }

      const notificationDate = new Date(date);
      const now = new Date();

      const seconds = Math.floor(
        (now - notificationDate) / 1000
      );

      if (seconds < 60) {
        return "Just now";
      }

      const minutes = Math.floor(seconds / 60);

      if (minutes < 60) {
        return `${minutes}m ago`;
      }

      const hours = Math.floor(minutes / 60);

      if (hours < 24) {
        return `${hours}h ago`;
      }

      const days = Math.floor(hours / 24);

      if (days < 7) {
        return `${days}d ago`;
      }

      return notificationDate.toLocaleDateString(
        "en-MY",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },
  },
};
</script>

<style scoped>
.notification-dropdown-menu {
  width: 380px;
  max-width: calc(100vw - 30px);
}

.notification-list {
  max-height: 420px;
  overflow-y: auto;
}

.notification-item {
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid var(--vz-border-color);
}

.notification-item:hover {
  background-color: var(--vz-light);
}

.notification-item.unread {
  background-color: rgba(var(--vz-primary-rgb), 0.04);
}

.notification-item.unread:hover {
  background-color: rgba(var(--vz-primary-rgb), 0.08);
}

.notification-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 18px;
}

.notification-unread-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--vz-danger);
  flex-shrink: 0;
}

.notification-message {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.topbar-badge {
  top: 6px;
  right: 2px;
}

.notification-loading {
  min-height: 200px;
}
</style>